import {
  Cancel,
  CheckCircle,
  Computer,
  Error as ErrorIcon,
  Group,
  HourglassEmpty,
  Person,
} from "@mui/icons-material";
import {
  Chip,
  CircularProgress,
  capitalize,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo } from "react";
import type { Task } from "../../types/chat";
import { subString } from "../../utils";

interface TaskListProps {
  tasks: Task[];
}

export const TaskList: React.FC<TaskListProps> = ({ tasks }) => {
  const getTaskIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle color="success" fontSize="small" />;
      case "failed":
        return <ErrorIcon color="error" fontSize="small" />;
      case "in_progress":
        return <CircularProgress size={16} color="success" />;
      case "cancelled":
        return <Cancel color="warning" fontSize="small" />;
      case "pending":
        return <CircularProgress size={16} color="success" />;
      default:
        return <HourglassEmpty fontSize="small" />;
    }
  };

  const getOwnerIcon = (ownerType: string) => {
    switch (ownerType) {
      case "manager":
        return <Computer fontSize="small" />;
      case "team":
        return <Group fontSize="small" />;
      case "user":
        return <Person fontSize="small" />;
      default:
        return <Computer fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "success";
      case "failed":
        return "error";
      case "in_progress":
        return "primary";
      case "cancelled":
      case "stopped":
        return "warning";
      case "pending":
        return "default";
      default:
        return "default";
    }
  };

  // Using toSorted() for immutability (React best practice: preserves original array)
  const recentTasks = useMemo(
    () =>
      tasks
        .filter((t) => t.type !== "done")
        .toSorted((a, b) => b.added_at - a.added_at)
        .slice(0, 10),
    [tasks],
  );

  return (
    <Stack spacing={2} divider={<Divider />}>
      {recentTasks.map((task) => (
        <Stack key={task.id} direction="row" alignItems="center" spacing={2}>
          {getTaskIcon(task.status)}
          <Stack flexGrow={1} direction="column" spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="column" spacing={0} alignItems="flex-start">
                <Typography variant="body2" fontWeight="medium">
                  {task.type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
                </Typography>
                {task.type === "delegate_task" && (
                  <Typography variant="caption" color="text.secondary">
                    {capitalize(task.input.delegated_task_name?.replace(/_/g, " ") ?? "")}
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  icon={
                    ["working", "waiting"].includes(task.status as string) ? (
                      <CircularProgress
                        size={12}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        color={getStatusColor(task.status) as any}
                      />
                    ) : undefined
                  }
                  label={capitalize(task.status.replace(/_/g, " "))}
                  size="small"
                  color={getStatusColor(task.status)}
                />
                <Tooltip title={`Owner: ${task.owner_type} (${task.owner_id || "unknown"})`}>
                  {getOwnerIcon(task.owner_type)}
                </Tooltip>
              </Stack>
            </Stack>
            <Stack spacing={1} width="100%">
              {task.input && (
                <Typography variant="caption">{task.input.task_description}</Typography>
              )}
              {task.output && (
                <Paper variant="outlined" sx={{ p: 1, backgroundColor: "grey.50", width: "100%" }}>
                  {/* TODO(taras): Implement per type output components */}
                  <Typography variant="caption" sx={{ whiteSpace: "pre-wrap" }}>
                    {["read_file"].includes(task.type)
                      ? subString(task.output?.text, 64)
                      : task.output.text}
                  </Typography>
                  <Stack direction="column" spacing={1} mt={0.5}>
                    {task.output.attached_file_names?.map((fileName) => (
                      <Chip
                        key={fileName}
                        label={subString(fileName, 32)}
                        size="small"
                        variant="outlined"
                        sx={{ width: "fit-content" }}
                      />
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" alignSelf="flex-end">
              at step {task.added_at}
            </Typography>
          </Stack>
        </Stack>
      ))}
      {recentTasks.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No recent tasks found.
        </Typography>
      )}
    </Stack>
  );
};
